import React, { useState, useEffect } from 'react'
import { Button, Input, Alert } from 'reactstrap'
import { Modal } from '@byscott-io/corebyscott'
import Lex from '../Shared/Lex'
import { getCsrfToken } from '../../utils/csrf'

const MfaSetup = ({ userStore }) => {
  const [mfaStatus, setMfaStatus] = useState(null)
  const [qrCode, setQrCode] = useState('')
  const [secret, setSecret] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [backupCodes, setBackupCodes] = useState([])
  const [showSetupModal, setShowSetupModal] = useState(false)
  const [showBackupCodesModal, setShowBackupCodesModal] = useState(false)
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [disableCode, setDisableCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState({})

  useEffect(() => {
    fetchMfaStatus()
  }, [])

  const fetchMfaStatus = async () => {
    try {
      const response = await fetch('/api/v1/mfa', {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || '',
          'X-CSRF-Token': getCsrfToken()
        }
      })
      const data = await response.json()
      setMfaStatus(data)
    } catch (error) {
      console.error('Error fetching MFA status:', error)
    }
  }

  const startSetup = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/v1/mfa/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || '',
          'X-CSRF-Token': getCsrfToken()
        }
      })
      const data = await response.json()
      
      if (response.ok) {
        setQrCode(data.qr_code)
        setSecret(data.secret)
        setShowSetupModal(true)
      } else {
        setErrors(data.errors || {})
      }
    } catch (error) {
      console.error('Error starting MFA setup:', error)
      setErrors({ general: ['An error occurred. Please try again.'] })
    } finally {
      setLoading(false)
    }
  }

  const enableMfa = async () => {
    setLoading(true)
    setErrors({})
    
    try {
      const response = await fetch('/api/v1/mfa/enable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || '',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          verification_code: verificationCode
        })
      })
      const data = await response.json()
      
      if (response.ok) {
        setMessage(data.message)
        setBackupCodes(data.backup_codes)
        setShowSetupModal(false)
        setShowBackupCodesModal(true)
        fetchMfaStatus()
        setVerificationCode('')
      } else {
        setErrors(data.errors || {})
      }
    } catch (error) {
      console.error('Error enabling MFA:', error)
      setErrors({ general: ['An error occurred. Please try again.'] })
    } finally {
      setLoading(false)
    }
  }

  const disableMfa = async () => {
    setLoading(true)
    setErrors({})
    
    try {
      const response = await fetch('/api/v1/mfa/disable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || '',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify({
          token: disableCode
        })
      })
      const data = await response.json()
      
      if (response.ok) {
        setMessage(data.message)
        setShowDisableModal(false)
        fetchMfaStatus()
        setDisableCode('')
      } else {
        setErrors(data.errors || {})
      }
    } catch (error) {
      console.error('Error disabling MFA:', error)
      setErrors({ general: ['An error occurred. Please try again.'] })
    } finally {
      setLoading(false)
    }
  }

  const viewBackupCodes = async () => {
    try {
      const response = await fetch('/api/v1/mfa/backup_codes', {
        headers: {
          'Accept': 'application/json',
          'JWT_AUD': 'WebApp',
          'Authorization': userStore.bearerToken || '',
          'X-CSRF-Token': getCsrfToken()
        }
      })
      const data = await response.json()
      
      if (response.ok) {
        setBackupCodes(data.backup_codes)
        setShowBackupCodesModal(true)
      }
    } catch (error) {
      console.error('Error fetching backup codes:', error)
    }
  }

  if (!mfaStatus) return <div>Loading...</div>

  return (
    <div className="mfa-section">
      <h4><Lex term="multi-factor-authentication" /></h4>
      
      {message && (
        <Alert color="success" onClose={() => setMessage('')} dismissible>
          {message}
        </Alert>
      )}
      
      {errors.general && (
        <Alert color="danger">{errors.general[0]}</Alert>
      )}

      <table className="p10">
        <tbody>
          <tr>
            <th className="text-right"><Lex term="status" /></th>
            <td>
              <span className={`badge ${mfaStatus.mfa_enabled ? 'badge-success' : 'badge-secondary'}`}>
                {mfaStatus.mfa_enabled ? 'Enabled' : 'Disabled'}
              </span>
            </td>
          </tr>
          <tr>
            <th className="text-right"><Lex term="actions" /></th>
            <td>
              {!mfaStatus.mfa_enabled ? (
                <Button 
                  color="primary" 
                  onClick={startSetup}
                  disabled={loading}
                >
                  {loading ? 'Setting up...' : 'Enable MFA'}
                </Button>
              ) : (
                <div>
                  <Button 
                    color="info" 
                    onClick={viewBackupCodes}
                    className="me-2"
                  >
                    View Backup Codes
                  </Button>
                  <Button 
                    color="danger" 
                    onClick={() => setShowDisableModal(true)}
                  >
                    Disable MFA
                  </Button>
                </div>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="cbs-scope">
        {/* MFA Setup Modal */}
        <Modal
          open={showSetupModal}
          onClose={() => setShowSetupModal(false)}
          title="Setup Multi-Factor Authentication"
          footer={
            <>
              <Button color="secondary" onClick={() => setShowSetupModal(false)}>Cancel</Button>
              <Button color="primary" onClick={enableMfa} disabled={loading || !verificationCode}>
                {loading ? 'Enabling...' : 'Enable MFA'}
              </Button>
            </>
          }
        >
          <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>

          <div className="text-center my-3" dangerouslySetInnerHTML={{ __html: qrCode }} />

          <p><small>Can&apos;t scan? Enter this code manually: <code>{secret}</code></small></p>

          <div className="form-group">
            <label>Enter verification code from your app:</label>
            <Input
              type="text"
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              maxLength="6"
            />
            {errors.verification_code && (
              <div className="text-danger">{errors.verification_code[0]}</div>
            )}
          </div>
        </Modal>

        {/* Backup Codes Modal */}
        <Modal
          open={showBackupCodesModal}
          onClose={() => setShowBackupCodesModal(false)}
          title="Your Backup Codes"
          footer={
            <>
              <Button color="secondary" onClick={() => window.print()}>Print Codes</Button>
              <Button color="primary" onClick={() => setShowBackupCodesModal(false)}>Done</Button>
            </>
          }
        >
          <Alert color="warning">
            <strong>Important:</strong> Save these codes in a safe place. Each can only be used once.
          </Alert>

          <div className="backup-codes-list">
            {backupCodes.map((code, index) => (
              <div key={index} className="backup-code">
                <code>{code}</code>
              </div>
            ))}
          </div>
        </Modal>

        {/* Disable MFA Modal */}
        <Modal
          open={showDisableModal}
          onClose={() => setShowDisableModal(false)}
          title="Disable Multi-Factor Authentication"
          footer={
            <>
              <Button color="secondary" onClick={() => setShowDisableModal(false)}>Cancel</Button>
              <Button color="danger" onClick={disableMfa} disabled={loading || !disableCode}>
                {loading ? 'Disabling...' : 'Disable MFA'}
              </Button>
            </>
          }
        >
          <Alert color="danger">
            <strong>Warning:</strong> Disabling MFA will make your account less secure.
          </Alert>

          <div className="form-group">
            <label>Enter your current authentication code:</label>
            <Input
              type="text"
              placeholder="000000"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              maxLength="6"
            />
            {errors.authentication && (
              <div className="text-danger">{errors.authentication[0]}</div>
            )}
          </div>
        </Modal>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .backup-codes-list {
          font-family: monospace;
          background: #f8f9fa;
          padding: 15px;
          border-radius: 4px;
          margin: 15px 0;
        }
        .backup-code {
          margin: 5px 0;
          font-size: 16px;
        }
        .badge {
          padding: 0.25em 0.6em;
          font-size: 75%;
          font-weight: 700;
          border-radius: 0.25rem;
        }
        .badge-success {
          color: #fff;
          background-color: #28a745;
        }
        .badge-secondary {
          color: #fff;
          background-color: #6c757d;
        }
      `}} />
    </div>
  )
}

export default MfaSetup